import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type {
  FlightInfo,
  FlightType,
  RecommendedSeats,
  Seat,
  SegmentWithPassengers,
} from "@repo/ui/types/seats";

import { useAppSelector } from "../hooks";

export interface SeatPriceDifferenceData {
  seat: Seat;
  seatLabel: string;
  difference: number;
}

export interface PrevSeat {
  outbound: {
    passenger?: string;
    seat?: string;
    seats?: string[];
  }[];
  inbound: {
    passenger?: string;
    seat?: string;
    seats?: string[];
  }[];
}

export interface PassengerSeatState {
  paxKey: string;
  paxOriginalSeat: string;
  paxCurrentSeat: string;
  isSeatTransferred: boolean;
  showPreviousSeatNotAvailable: boolean;
  showRetainedSeatMessage: boolean;
}

export interface SegmentSeatState {
  segment: string;
  passengers: PassengerSeatState[];
  isSegmentHasUntransferredSeat: boolean;
}

export interface JourneySeatState {
  flightType: "outbound" | "inbound";
  segments: SegmentSeatState[];
  isJourneyHasUntransferredSeat: boolean;
}

export interface SeatMapState {
  outbound: SegmentWithPassengers[];
  inbound: SegmentWithPassengers[];
  isHydrated: boolean;
  selectedPassengerId: number | null;
  currentFlightType: FlightType;
  currentSelectedSegment: FlightInfo | null;
  recordLocator?: string;
  backendConfirmedSeats: BackendSeatAssignment[];
  recommendedSeatsOutbound?: RecommendedSeats | null;
  recommendedSeatsInbound?: RecommendedSeats | null;
  prevSeat: PrevSeat;
  pendingSeatAssignments: boolean;
  pendingSeatRefresh: boolean;
  seatMapRefreshCounter: number;
  hasAutoSelectedFlightType: boolean;
  computedSeatState: JourneySeatState[];
}

export interface BackendSeatAssignment {
  journeyKey: string;
  passengerKey: string;
  unitDesignator: string;
  unitKey: string;
  departureStation?: string;
  arrivalStation?: string;
}

const initialState: SeatMapState = {
  outbound: [],
  inbound: [],
  isHydrated: false,
  selectedPassengerId: null,
  currentFlightType: "outbound",
  currentSelectedSegment: null,
  backendConfirmedSeats: [],
  recommendedSeatsOutbound: null,
  recommendedSeatsInbound: null,
  prevSeat: { outbound: [], inbound: [] },
  pendingSeatAssignments: false,
  pendingSeatRefresh: false,
  seatMapRefreshCounter: 0,
  hasAutoSelectedFlightType: false,
  computedSeatState: [],
};

const seatMapSlice = createSlice({
  name: "seatMap",
  initialState,
  reducers: {
    setSeatMap: (
      state,
      action: PayloadAction<Pick<SeatMapState, "outbound" | "inbound" | "recordLocator">>,
    ) => {
      const prevFlightType = state.currentFlightType;
      const prevSegment = state.currentSelectedSegment;

      // If this is a fresh initialization (not hydrated yet), start with outbound
      // The component will switch to inbound if needed based on business logic
      state.currentFlightType = "outbound";
      state.currentSelectedSegment = action.payload.outbound?.[0]?.flightInfo || null;
      // state.selectedPassengerId =
      //   action.payload.outbound?.[0]?.passengers?.[0]?.id ?? null;
      const addSeatSource = (segments: SegmentWithPassengers[]) =>
        segments.map((seg) => ({
          ...seg,
          passengers: seg.passengers.map((p) => ({
            ...p,
            seatSource: p.seat ? ("backend" as const) : undefined, // ✅ use literal type
          })),
        }));

      state.outbound = addSeatSource(action.payload.outbound);
      state.inbound = addSeatSource(action.payload.inbound);

      //Guard to Make sure the tab doesnt change (current segment) when the seats are removed or added for a passenger
      const prevList = prevFlightType === "outbound" ? state.outbound : state.inbound;
      const stillExists =
        prevSegment &&
        prevList?.some(
          (seg) =>
            seg.flightInfo?.arrivalStation === prevSegment.arrivalStation &&
            seg.flightInfo?.departureStation === prevSegment.departureStation,
        );

      // For connecting flights (multiple segments), always reset to first segment to ensure consistent UX
      const isConnectingFlight =
        (prevFlightType === "outbound" ? state.outbound : state.inbound)?.length > 1;
      const shouldResetToFirst = isConnectingFlight && stillExists;

      state.currentFlightType = stillExists && !shouldResetToFirst ? prevFlightType : "outbound";
      state.currentSelectedSegment =
        stillExists && !shouldResetToFirst
          ? prevSegment
          : action.payload.outbound?.[0]?.flightInfo || null;

      state.isHydrated = true;

      if (action.payload.recordLocator) {
        state.recordLocator = action.payload.recordLocator;
      } else {
        // clearing it on normal booking flow
        state.recordLocator = undefined;
      }
    },

    setSelectedPassengerId: (state, action: PayloadAction<number | null>) => {
      if (action.payload !== state.selectedPassengerId) {
        state.selectedPassengerId = action.payload;
      }
    },

    setPrevSeat: (state, action: PayloadAction<PrevSeat>) => {
      state.prevSeat = action.payload;
    },

    removeSeatPriceDifference: (
      state,
      action: PayloadAction<{ passengerId: number; flightType: FlightType }>,
    ) => {
      const { passengerId, flightType } = action.payload;

      const list1 = flightType === "outbound" ? state.outbound : state.inbound;
      const currentSegment = list1?.find(
        (seg) =>
          seg.flightInfo?.arrivalStation === state.currentSelectedSegment?.arrivalStation &&
          seg.flightInfo?.departureStation === state.currentSelectedSegment?.departureStation,
      );

      if (!currentSegment?.seatPriceDifferences) return;

      currentSegment.seatPriceDifferences = currentSegment.seatPriceDifferences.filter(
        (item) => item.passengerId !== passengerId,
      );
    },

    setSeatPriceDifference: (
      state,
      action: PayloadAction<{
        passengerId: number;
        flightType: FlightType;
        data: SeatPriceDifferenceData;
      }>,
    ) => {
      const { flightType, passengerId, data } = action.payload;

      const currentSegment = state[flightType]?.find(
        (seg) =>
          seg.flightInfo?.arrivalStation === state.currentSelectedSegment?.arrivalStation &&
          seg.flightInfo?.departureStation === state.currentSelectedSegment?.departureStation,
      );

      if (!currentSegment) return;

      if (!currentSegment.seatPriceDifferences) {
        currentSegment.seatPriceDifferences = [];
      }

      // Check if passenger already has a record, update it
      const existing = currentSegment.seatPriceDifferences.find(
        (item) => item.passengerId === passengerId,
      );

      if (existing) {
        existing.data = data;
      } else {
        currentSegment.seatPriceDifferences.push({ passengerId, data });
      }
    },

    setCurrentFlightType: (state, action: PayloadAction<"outbound" | "inbound">) => {
      state.currentFlightType = action.payload;

      const currentSelectedSegment =
        (action.payload === "outbound" ? state.outbound : state.inbound)[0]?.flightInfo || null;
      state.currentSelectedSegment = currentSelectedSegment;

      // //find the passenger without a seat in the state
      const passengerWithoutSeat = state[action.payload]
        .find(
          (flight) =>
            flight.flightInfo?.arrivalStation === currentSelectedSegment?.arrivalStation &&
            flight.flightInfo?.departureStation === currentSelectedSegment?.departureStation,
        )
        ?.passengers.find((passenger) => passenger.seat === null);

      if (passengerWithoutSeat) {
        state.selectedPassengerId = passengerWithoutSeat.id;
      }
    },

    setCurrentSelectedSegment: (state, action: PayloadAction<FlightInfo | null>) => {
      state.currentSelectedSegment = action.payload;

      // //find the passenger without a seat in the state
      const passengerWithoutSeat = state[state.currentFlightType]
        .find(
          (flight) =>
            flight.flightInfo?.arrivalStation === action.payload?.arrivalStation &&
            flight.flightInfo?.departureStation === action.payload?.departureStation,
        )
        ?.passengers.find((passenger) => passenger.seat === null);

      if (passengerWithoutSeat) {
        state.selectedPassengerId = passengerWithoutSeat.id;
      }
    },

    setSeatAssignments: (
      state,
      action: PayloadAction<{
        passengerId: number;
        seat: Seat;
        source?: "user" | "backend" | "checkin";
        moveToPassengerWithoutSeat?: boolean;
      }>,
    ) => {
      const { passengerId, seat, source, moveToPassengerWithoutSeat = true } = action.payload;

      const flightType = state.currentFlightType;
      const list2 = flightType === "outbound" ? state.outbound : state.inbound;
      const flight = list2?.find(
        (flight) =>
          flight.flightInfo?.arrivalStation === state.currentSelectedSegment?.arrivalStation &&
          flight.flightInfo?.departureStation === state.currentSelectedSegment?.departureStation,
      );

      // find the passenger
      const passenger = flight?.passengers.find((passenger) => passenger.id === passengerId);

      // update seat
      passenger!.seatSource = source || "user";

      // update seat
      passenger!.seat = seat;

      // //find the passenger without a seat in the state
      const passengerWithoutSeat = state[flightType]
        .find(
          (flight) =>
            flight.flightInfo?.arrivalStation === state.currentSelectedSegment?.arrivalStation &&
            flight.flightInfo?.departureStation === state.currentSelectedSegment?.departureStation,
        )
        ?.passengers.find((passenger) => passenger.seat === null);

      if (passengerWithoutSeat && moveToPassengerWithoutSeat) {
        state.selectedPassengerId = passengerWithoutSeat.id;
      }

      // 🔹 If the seat was assigned from check-in flow, do NOT affect pricing
      if (source === "checkin") {
        return;
      }
    },

    setSeatAssignmentsBatch: (
      state,
      action: PayloadAction<{
        assignments: {
          passengerId: number;
          seat: Seat;
          source?: "user" | "backend" | "checkin";
        }[];
        flightType?: FlightType;
      }>,
    ) => {
      const { assignments, flightType: actionFlightType } = action.payload;
      if (assignments.length === 0) return;

      const flightType = actionFlightType || state.currentFlightType;
      const flights = flightType === "outbound" ? state.outbound : state.inbound;

      let targetFlight = flights?.find(
        (f) =>
          f.flightInfo?.arrivalStation === state.currentSelectedSegment?.arrivalStation &&
          f.flightInfo?.departureStation === state.currentSelectedSegment?.departureStation,
      );

      if (!targetFlight && flights && flights.length > 0) {
        targetFlight = flights[0];
      }

      if (!targetFlight) return;

      for (const { passengerId, seat, source } of assignments) {
        const passenger = targetFlight.passengers.find((p) => p.id === passengerId);
        if (passenger) {
          passenger.seatSource = source ?? "user";
          passenger.seat = seat;
        }
      }
    },

    setRecommendedSeats: (
      state,
      action: PayloadAction<{
        flightType: "outbound" | "inbound";
        recommendedSeats: RecommendedSeats;
      }>,
    ) => {
      const key =
        action.payload.flightType === "outbound"
          ? "recommendedSeatsOutbound"
          : "recommendedSeatsInbound";

      state[key] = action.payload.recommendedSeats;
    },

    resetAllRecommendedSeats: (state) => {
      state.recommendedSeatsOutbound = null;
      state.recommendedSeatsInbound = null;
    },

    removeSeat: (state, action: PayloadAction<{ passengerId: number }>) => {
      const { passengerId } = action.payload;
      // find passenger by id and update seat
      const flightType = state.currentFlightType;
      const list3 = flightType === "outbound" ? state.outbound : state.inbound;
      const segment = list3?.find(
        (seg) =>
          seg.flightInfo?.arrivalStation === state.currentSelectedSegment?.arrivalStation &&
          seg.flightInfo?.departureStation === state.currentSelectedSegment?.departureStation,
      );
      const passenger = list3
        .find(
          (flight) =>
            flight.flightInfo?.arrivalStation === state.currentSelectedSegment?.arrivalStation &&
            flight.flightInfo?.departureStation === state.currentSelectedSegment?.departureStation,
        )
        ?.passengers.find((passenger) => passenger.id === passengerId);
      // update seat
      passenger!.seat = null;

      // ✅ Also remove seat price difference
      if (segment?.seatPriceDifferences) {
        segment.seatPriceDifferences = segment.seatPriceDifferences.filter(
          (item) => item.passengerId !== passengerId,
        );
      }

      // if every passengers in the current segment has no seat, set the current selected segment to the first segment
      // const allSeatsCleared = state[flightType]
      //   .find(
      //     (flight) =>
      //       flight.flightInfo?.arrivalStation === state.currentSelectedSegment?.arrivalStation &&
      //       flight.flightInfo?.departureStation === state.currentSelectedSegment?.departureStation,
      //   )
      //   ?.passengers.every((passenger) => passenger.seat === null);
      // if (allSeatsCleared) {
      //   state.currentSelectedSegment = state[flightType][0]?.flightInfo || null;
      // }

      // //find the passenger without a seat in the state
      // const passengerWithoutSeat = state[flightType]
      //   .find(
      //     (flight) =>
      //       flight.flightInfo?.arrivalStation ===
      //         state.currentSelectedSegment?.arrivalStation &&
      //       flight.flightInfo?.departureStation ===
      //         state.currentSelectedSegment?.departureStation
      //   )
      //   ?.passengers.find((passenger) => passenger.seat === null);

      // if (passengerWithoutSeat) {
      //   state.selectedPassengerId = passengerWithoutSeat.id;
      // }
    },

    setBackendConfirmedSeats: (state, action: PayloadAction<BackendSeatAssignment[]>) => {
      state.backendConfirmedSeats = action.payload;
    },

    resetSeatMap: (state) => {
      return {
        ...initialState,
        prevSeat: state.prevSeat,
        backendConfirmedSeats: state.backendConfirmedSeats,
        hasAutoSelectedFlightType: false,
      };
    },

    clearSeatsByFlightType: (state, action: PayloadAction<{ flightType: FlightType }>) => {
      const { flightType } = action.payload;
      const flights = state[flightType];

      flights?.forEach((flight) => {
        flight.passengers?.forEach((passenger) => {
          passenger.seat = null;
        });
        flight.seatPriceDifferences = [];
      });
    },

    setSeatAssignmentPending: (state, action: PayloadAction<boolean>) => {
      state.pendingSeatAssignments = action.payload;
    },
    setPendingSeatRefresh: (state, action: PayloadAction<boolean>) => {
      state.pendingSeatRefresh = action.payload;
    },

    setSeatMapHydrated: (state, action: PayloadAction<boolean>) => {
      state.isHydrated = action.payload;
    },

    incrementSeatMapRefreshCounter: (state) => {
      state.seatMapRefreshCounter += 1;
    },

    setHasAutoSelectedFlightType: (state, action: PayloadAction<boolean>) => {
      state.hasAutoSelectedFlightType = action.payload;
    },

    setComputedSeatState: (state, action: PayloadAction<JourneySeatState[]>) => {
      state.computedSeatState = action.payload;
    },

    clearComputedSeatState: (state) => {
      state.computedSeatState = [];
    },
  },
});

// -------------------- Selectors (fixed) --------------------

export const useOutbound = () => useAppSelector((state) => state.seatMap?.outbound);
export const useInBound = () => useAppSelector((state) => state.seatMap?.inbound);

export const useFlightType = () =>
  useAppSelector((state) => state.seatMap?.currentFlightType ?? "outbound");

export const useCurrentSelectedSegment = () =>
  useAppSelector((state) => state.seatMap?.currentSelectedSegment);

export const useBackendConfirmedSeats = () =>
  useAppSelector((state) => state.seatMap?.backendConfirmedSeats);

export const useRecommendedSeats = (
  flightType: "outbound" | "inbound",
): RecommendedSeats | null => {
  return useAppSelector((state) =>
    flightType === "outbound"
      ? state.seatMap?.recommendedSeatsOutbound || null
      : state.seatMap?.recommendedSeatsInbound || null,
  );
};

export const useSeatAssignmentPending = () =>
  useAppSelector((state) => state.seatMap?.pendingSeatAssignments ?? false);

export const useSeatMapRefreshCounter = () =>
  useAppSelector((state) => state.seatMap?.seatMapRefreshCounter ?? 0);

export const usePendingSeatRefresh = () =>
  useAppSelector((state) => state.seatMap?.pendingSeatRefresh ?? false);

export const useHasAutoSelectedFlightType = () =>
  useAppSelector((state) => state.seatMap?.hasAutoSelectedFlightType ?? false);

export const useComputedSeatState = () =>
  useAppSelector((state) => state.seatMap.computedSeatState);

export const useJourneyUntransferred = (flightType: "outbound" | "inbound") =>
  useAppSelector(
    (state) =>
      state.seatMap.computedSeatState.find((j) => j.flightType === flightType)
        ?.isJourneyHasUntransferredSeat ?? false,
  );

export const useSegmentUntransferred = (flightType: "outbound" | "inbound", segmentKey: string) =>
  useAppSelector(
    (state) =>
      state.seatMap.computedSeatState
        .find((j) => j.flightType === flightType)
        ?.segments.find((s) => s.segment === segmentKey)?.isSegmentHasUntransferredSeat ?? false,
  );

export const {
  setSeatMap,
  setSeatAssignments,
  setSeatAssignmentsBatch,
  removeSeat,
  setSelectedPassengerId,
  setCurrentFlightType,
  setSeatPriceDifference,
  setCurrentSelectedSegment,
  removeSeatPriceDifference,
  setBackendConfirmedSeats,
  resetSeatMap,
  setRecommendedSeats,
  resetAllRecommendedSeats,
  clearSeatsByFlightType,
  setPrevSeat,
  setSeatAssignmentPending,
  setSeatMapHydrated,
  incrementSeatMapRefreshCounter,
  setHasAutoSelectedFlightType,
  setComputedSeatState,
  clearComputedSeatState,
} = seatMapSlice.actions;
export default seatMapSlice.reducer;
